/**
 * Faction event popup — modal shown when a faction requests help
 */

import type { FactionEventDef } from '../game/data/factions.js';
import { getFactionDef, AD_BONUS_FAVOR } from '../game/data/factions.js';
import type { GameState, GameAction, FactionId } from '../game/index.js';
import { getTotalCps } from '../game/index.js';
import { formatNumber } from '../utils/format.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { AdManager, getAdRewards } from '../monetization/index.js';

type Dispatch = (action: GameAction) => void;
type GetState = () => GameState;

let overlayEl: HTMLDivElement | null = null;
let timerHandle: ReturnType<typeof setInterval> | null = null;
let dispatchRef: Dispatch | null = null;
let getStateRef: GetState | null = null;

export function initFactionEventPopup(
  dispatch: Dispatch,
  getState: GetState,
): void {
  dispatchRef = dispatch;
  getStateRef = getState;
}

export function showFactionEvent(evtDef: FactionEventDef): void {
  if (overlayEl) hideFactionEvent();
  if (!dispatchRef || !getStateRef) return;

  const fDef = getFactionDef(evtDef.factionId);
  const tDef = getFactionDef(evtDef.targetFactionId);
  if (!fDef || !tDef) return;

  const state = getStateRef();
  const ipReward = Math.floor(getTotalCps(state) * evtDef.ipRewardMultiplier);

  overlayEl = document.createElement('div');
  overlayEl.className = 'faction-event-overlay';
  overlayEl.style.setProperty('--faction-color', fDef.color);

  const rewardText = t('faction_event_reward')
    .replace('{favor}', String(evtDef.helpFavorGain))
    .replace('{faction}', t(fDef.nameKey))
    .replace('{loss}', String(evtDef.helpFavorLoss))
    .replace('{target}', t(tDef.nameKey));

  const ipText = t('faction_event_ip_reward')
    .replace('{amount}', formatNumber(ipReward));

  overlayEl.innerHTML = `
    <div class="faction-event-card" style="border-color:${fDef.color}">
      <img class="faction-event-logo" src="${fDef.imagePath}" alt=""
           style="background:${fDef.color}30;border:2px solid ${fDef.color}"
           onerror="this.style.display='none'" />
      <div class="faction-event-title" style="color:${fDef.color}">${t(evtDef.titleKey)}</div>
      <div class="faction-event-desc">${t(evtDef.descKey)}</div>
      <div class="faction-event-rewards">${rewardText}<br>${ipText}</div>
      <div class="faction-event-timer" id="faction-event-timer"></div>
      <div class="faction-event-actions" id="faction-event-actions">
        <button class="faction-event-btn-help" id="faction-event-help">${t('faction_event_help')}</button>
        <button class="faction-event-btn-ignore" id="faction-event-ignore">${t('faction_event_ignore')}</button>
      </div>
    </div>
  `;

  document.body.appendChild(overlayEl);
  playSfx('achievement');

  // Timer
  const updateTimer = () => {
    const timerEl = document.getElementById('faction-event-timer');
    if (!timerEl || !getStateRef) return;
    const st = getStateRef();
    const ev = st.faction.currentEvent;
    if (!ev) { hideFactionEvent(); return; }
    const remaining = Math.max(0, Math.ceil((ev.expiresAt - Date.now()) / 1000));
    timerEl.textContent = t('faction_event_timer').replace('{seconds}', String(remaining));
    if (remaining <= 0) hideFactionEvent();
  };
  updateTimer();
  timerHandle = setInterval(updateTimer, 1000);

  // Help button
  document.getElementById('faction-event-help')?.addEventListener('click', () => {
    if (!dispatchRef || !getStateRef) return;
    const helpFactionId = evtDef.factionId;
    dispatchRef({ type: 'HELP_FACTION_EVENT' });
    playSfx('boost');

    // Show ad bonus option
    showAdBonusOption(helpFactionId);
  });

  // Ignore button
  document.getElementById('faction-event-ignore')?.addEventListener('click', () => {
    if (!dispatchRef) return;
    dispatchRef({ type: 'IGNORE_FACTION_EVENT' });
    hideFactionEvent();
  });
}

function showAdBonusOption(factionId: FactionId): void {
  if (!overlayEl || !dispatchRef) return;
  if (timerHandle) { clearInterval(timerHandle); timerHandle = null; }

  const actionsEl = document.getElementById('faction-event-actions');
  if (!actionsEl) { hideFactionEvent(); return; }

  const timerEl = document.getElementById('faction-event-timer');
  if (timerEl) timerEl.style.display = 'none';

  const fDef = getFactionDef(factionId);
  const factionName = fDef ? t(fDef.nameKey) : '';

  actionsEl.innerHTML = `
    <button class="faction-event-btn-ad" id="faction-event-ad">
      ${t('faction_event_ad_bonus').replace('{n}', String(AD_BONUS_FAVOR)).replace('{faction}', factionName)}
    </button>
    <button class="faction-event-btn-ignore" id="faction-event-close" style="margin-top:8px">${t('close')}</button>
  `;

  document.getElementById('faction-event-ad')?.addEventListener('click', () => {
    if (!dispatchRef) return;
    const dispatch = dispatchRef;
    const rewards = getAdRewards();
    AdManager.showRewardedAd(
      rewards.INCOME_X5,
      () => {
        dispatch({ type: 'AD_FACTION_EVENT_BONUS', payload: { factionId } });
        playSfx('boost');
        hideFactionEvent();
      },
      () => { hideFactionEvent(); },
    );
  });

  document.getElementById('faction-event-close')?.addEventListener('click', () => {
    hideFactionEvent();
  });
}

export function hideFactionEvent(): void {
  if (timerHandle) { clearInterval(timerHandle); timerHandle = null; }
  if (overlayEl) { overlayEl.remove(); overlayEl = null; }
}
